import _styled, { default as _styled2, default as _styled3 } from "styled-components";
import { takeFirst } from "~/features/app/utils";
import { Asset, type Props as AssetProps } from "~/features/craft/asset";
import { type Provider as VideoProvider } from "~/features/ui/video/external-video";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { hasItems } from "~/features/app/utils";
import { buildPosterSrc } from "~/features/ui/video/utils";
import { Caption } from "~/features/ui/caption";
const fragment = graphql(`
  fragment Media on baseMedia_MatrixField {
    ... on baseMedia_image_BlockType {
      imageAsset {
        ...Asset
      }
      caption
      isDarkened
    }
    ... on baseMedia_inlineVideo_BlockType {
      videoAsset {
        ...Asset
      }
      videoUrl
      posterImage {
        ... on default_Asset {
          src: url
        }
      }
      caption
      isDarkened
    }
    ... on baseMedia_externalVideo_BlockType {
      coverMedia {
        ...Asset
      }
      videoProvider
      videoId
      showControls
      caption
      isDarkened
    }
    ... on baseMedia_audio_BlockType {
      audioAsset {
        ...Asset
      }
      coverMedia {
        ...Asset
      }
      caption
    }
  }
`);
type Props = FragmentType<typeof fragment> & Omit<Pick<AssetProps, "imageProps" | "inlineVideoProps" | "externalVideoProps" | "audioProps">, "videoId" | "videoProvider"> & {
  showCaption?: boolean;
  children?: React.ReactNode;
};
type AssetWrapperProps = {
  fit?: "contain" | "cover" | "fill";
  children: React.ReactNode;
  caption?: string;
  showCaption?: boolean;
  isDarkened?: boolean;
};
function AssetWrapper(props: AssetWrapperProps) {
  const {
    fit,
    children,
    caption,
    showCaption,
    isDarkened
  } = props;
  return <_StyledDiv $_css={[{
    "display": "flex",
    "width": "100%",
    "flexDirection": "column"
  }, fit === "cover" ? {
    "position": "absolute",
    "inset": "0",
    "height": "100%"
  } : undefined]}>
      <_StyledDiv2 $_css2={[{
      "position": "relative",
      "width": "100%"
    }, fit === "cover" ? {
      "height": "100%"
    } : undefined]}>
        {children}
        {isDarkened && <_StyledDiv3 $_css3={[{
        "pointerEvents": "none",
        "position": "absolute",
        "inset": "0",
        "zIndex": "10"
      }, {
        "backgroundImage": "linear-gradient(353deg,rgba(0,0,0,0.80) -4.45%,rgba(0,0,0,0.48) 27.74%),linear-gradient(188deg,rgba(0,0,0,0.80) 17.38%,rgba(0,0,0,0.00) 70.56%)"
      }]} />}
      </_StyledDiv2>
      {caption && showCaption && <Caption caption={caption} />}
    </_StyledDiv>;
}
export function Media(props: Props) {
  const {
    imageProps,
    inlineVideoProps,
    externalVideoProps,
    audioProps,
    showCaption = false,
    children,
    ...rest
  } = props;
  const media = useFragment(fragment, rest);
  if (media && "videoUrl" in media && media.videoUrl) {
    const {
      videoUrl,
      posterImage,
      caption,
      isDarkened
    } = media;
    const {
      fit
    } = inlineVideoProps || {};
    return <AssetWrapper fit={fit} caption={caption} showCaption={showCaption} isDarkened={isDarkened}>
        <Asset inlineVideoProps={{
        src: videoUrl,
        poster: buildPosterSrc(takeFirst(posterImage)?.src),
        ...inlineVideoProps
      }} />
      </AssetWrapper>;
  }
  if (media && "videoAsset" in media && hasItems(media.videoAsset)) {
    const {
      videoAsset,
      posterImage,
      caption,
      isDarkened
    } = media;
    const {
      fit
    } = inlineVideoProps || {};
    const video = takeFirst(videoAsset);
    return <AssetWrapper fit={fit} caption={caption} showCaption={showCaption} isDarkened={isDarkened}>
        <Asset asset={video} inlineVideoProps={{
        poster: buildPosterSrc(takeFirst(posterImage)?.src),
        ...inlineVideoProps
      }} />
      </AssetWrapper>;
  }
  if (media && "videoId" in media && media.videoId) {
    const {
      videoId,
      videoProvider,
      coverMedia,
      caption,
      isDarkened
    } = media;
    const showVideoControls = "showControls" in media ? media.showControls as boolean : false;
    const {
      fit
    } = externalVideoProps || {};
    // A section that supplies an inline play/pause overlay (inlineVideoProps.controls)
    // wants that same affordance for the video regardless of provider. For file
    // videos the inline player renders it; a video migrated to Mux takes the
    // external path, so restore the toggle there instead of silently backgrounding
    // it. Keep autoplay/loop (toggle, not showControls) so behavior stays identical.
    const wantsToggleControls = videoProvider === "mux" && !showVideoControls && Boolean(inlineVideoProps?.controls);
    const shouldUseBackgroundMuxVideo = videoProvider === "mux" && !showVideoControls && !wantsToggleControls && externalVideoProps?.background === undefined;
    return <AssetWrapper fit={fit} caption={caption} showCaption={showCaption} isDarkened={isDarkened}>
        <Asset externalVideoProps={{
        cover: <Asset asset={takeFirst(coverMedia)} inlineVideoProps={{
          fit: "cover"
        }} imageProps={{
          fit: "cover",
          sizes: "lg:50vw",
          imgixProps: {
            q: 50
          },
          // Forward caller imageProps so background heroes can
          // mark the cover as the LCP element (priority,
          // loading="eager", fetchPriority="high", sizes).
          ...imageProps
        }} />,
        videoId,
        videoProvider: videoProvider as VideoProvider,
        showControls: showVideoControls,
        ...(shouldUseBackgroundMuxVideo ? {
          background: true
        } : {}),
        ...(wantsToggleControls ? {
          toggleControls: true
        } : {}),
        ...externalVideoProps
      }} />
      </AssetWrapper>;
  }
  if (media && "imageAsset" in media && hasItems(media.imageAsset)) {
    const {
      imageAsset,
      caption,
      isDarkened
    } = media;
    const {
      fit
    } = imageProps || {};
    return <AssetWrapper fit={fit} caption={caption} showCaption={showCaption} isDarkened={isDarkened}>
        <Asset asset={takeFirst(imageAsset)} imageProps={imageProps} />
        {children}
      </AssetWrapper>;
  }
  if (media && "audioAsset" in media && hasItems(media.audioAsset)) {
    const {
      audioAsset,
      coverMedia,
      caption
    } = media;
    const {
      fit
    } = audioProps || {};
    return <AssetWrapper fit={fit} caption={caption} showCaption={showCaption}>
        <Asset asset={takeFirst(audioAsset)} audioProps={{
        cover: <Asset asset={takeFirst(coverMedia)} inlineVideoProps={{
          fit: "cover"
        }} imageProps={{
          fit: "cover",
          sizes: "lg:50vw",
          imgixProps: {
            q: 50
          }
        }} />,
        ...audioProps
      }} />
      </AssetWrapper>;
  }
  return null;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-1b2snwu-0"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-1b2snwu-1"
})`${p => p.$_css2}`;
var _StyledDiv3 = /*#__PURE__*/_styled3("div").withConfig({
  displayName: "media___StyledDiv3",
  componentId: "sc-mjueza-0"
})(["", ""], p => p.$_css3);